import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Trash2, Save, Building2, Copy, Cuboid, AlignCenter, FlipHorizontal, ArrowRightLeft, Sparkles, RotateCw, Printer, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DECO_PRESETS, DecorationLayer } from "@/components/DecorationSvg";

const newDecoId = () => `deco_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Admin — Säle (Halls) verwalten.
 * Jeder Saal hat freie Sitztypen (Name, Farbe, Aufpreis, override_price, sellable)
 * und einen visuellen Sitzplan (Reihen A–Z, Sitze pro Reihe mit kind + blocked).
 */
const NEW_ROW_LABEL = (existing) => {
  const used = new Set(existing.map((r) => r.label));
  for (let c = 65; c < 91; c++) {
    const l = String.fromCharCode(c);
    if (!used.has(l)) return l;
  }
  return "?";
};

const DEFAULT_TYPES = [
  { key: "regular", label: "Regulär", color: "#F5F0E6", surcharge: 0, override_price: null, sellable: true, icon: "seat" },
];

/** Vorgefertigte Sitz-Typen zum One-Click-Hinzufügen. */
const SEAT_PRESETS = [
  { key: "vip", label: "VIP / Loge", color: "#D4AF37", surcharge: 4, override_price: null, sellable: true, icon: "star" },
  { key: "sofa", label: "Sofa (2-Sitzer)", color: "#7A1921", surcharge: 6, override_price: null, sellable: true, icon: "sofa" },
  { key: "loveseat", label: "Loveseat", color: "#EE9CA1", surcharge: 5, override_price: null, sellable: true, icon: "heart" },
  { key: "wheelchair", label: "Rollstuhl-Platz", color: "#8FBC8F", surcharge: 0, override_price: 0, sellable: true, icon: "wheelchair" },
  { key: "gap", label: "Gang / Lücke", color: "#333333", surcharge: 0, override_price: null, sellable: false, icon: "" },
  { key: "wall", label: "Wand", color: "#3A2A20", surcharge: 0, override_price: null, sellable: false, icon: "wall" },
  { key: "stairs", label: "Treppe", color: "#8B7328", surcharge: 0, override_price: null, sellable: false, icon: "stairs" },
  { key: "pillar", label: "Pfeiler", color: "#5B5148", surcharge: 0, override_price: null, sellable: false, icon: "pillar" },
];

const emptyHall = () => ({
  name: "",
  hall: "Saal 1",
  seat_types: DEFAULT_TYPES.map((t) => ({ ...t })),
  rows: [{ label: "A", seats: [1,2,3,4].map((n) => ({ n, kind: "regular", blocked: false })) }],
});

export default function Halls() {
  const [halls, setHalls] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/seat-plans"); setHalls(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (h) => {
    if (!window.confirm(`Saal „${h.name}" löschen?`)) return;
    try { await api.delete(`/seat-plans/${h.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const duplicate = (h) => {
    setEditing({
      ...h, id: undefined,
      name: `${h.name} (Kopie)`,
      seat_types: (h.seat_types || DEFAULT_TYPES).map((t) => ({ ...t })),
      rows: (h.rows || []).map((r) => ({ label: r.label, seats: r.seats.map((s) => ({ ...s })) })),
    });
  };

  return (
    <div className="space-y-6" data-testid="halls-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Kino-Verwaltung</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><Building2 size={30} className="text-[#D4AF37]" /> Säle</h1>
        </div>
        <button data-testid="hall-new-btn" onClick={() => setEditing(emptyHall())}
          className="bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2 transitions-safe">
          <Plus size={14} /> Neuer Saal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {halls.map((h) => (
          <div key={h.id} className="card-lux p-4" data-testid={`hall-card-${h.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-2xl">{h.name || "—"}</div>
                <div className="text-xs text-[#A8A398] mt-1">
                  {h.hall} · {h.rows?.length || 0} Reihen · <span className="text-[#D4AF37]">{h.capacity ?? "—"} buchbare Sitze</span>
                </div>
                {h.seat_types?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {h.seat_types.map((t) => (
                      <span key={t.key} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ borderColor: t.color, color: t.color }}>
                        {t.label}{t.surcharge ? ` +${t.surcharge}€` : ""}{t.override_price === 0 ? " · frei" : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <SeatLabelsButton hall={h} />
                <button onClick={() => duplicate(h)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37]" title="Duplizieren"><Copy size={12} /></button>
                <button onClick={() => setEditing(h)} data-testid={`hall-edit-${h.id}`} className="text-xs px-3 py-1 border border-[#D4AF37] text-[#D4AF37] rounded hover:bg-[#D4AF37]/10">Bearbeiten</button>
                <button onClick={() => remove(h)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
              </div>
            </div>
            <MiniPlan hall={h} />
          </div>
        ))}
        {!halls.length && <div className="col-span-full text-center text-[#A8A398] py-16">Noch keine Säle. Lege den ersten Saal an.</div>}
      </div>

      {editing && <HallEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SeatLabelsButton({ hall }) {
  const [busy, setBusy] = useState(false);
  const seatCount = useMemo(() => {
    let c = 0;
    (hall.rows || []).forEach((r) => (r.seats || []).forEach((s) => {
      if (!s?.blocked) c++;
    }));
    return c;
  }, [hall]);

  const preview = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/signage/seat-labels",
        { hall_id: hall.id, hall: hall.hall || hall.name || "" },
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Erzeugen der Sitzschilder");
    } finally {
      setBusy(false);
    }
  };

  const printOffice = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Alle ${seatCount} Sitzschilder von „${hall.name}" an den Bürodrucker senden?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/signage/seat-labels?print_office=true",
        { hall_id: hall.id, hall: hall.hall || hall.name || "" });
      toast.success(`${seatCount} Sitzschilder an Drucker gesendet`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Drucken");
    } finally {
      setBusy(false);
    }
  };

  if (!hall.id) return null;
  return (
    <div className="inline-flex rounded border border-[#2A2A2A] overflow-hidden">
      <button
        onClick={preview}
        disabled={busy}
        data-testid={`hall-seat-labels-preview-${hall.id}`}
        title={`Sitzplan als PDF (${seatCount} Etiketten) – Vorschau`}
        className="text-xs px-2 py-1 text-[#EEDC82] hover:bg-[#EEDC82]/10 disabled:opacity-40 flex items-center gap-1">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
      </button>
      <button
        onClick={printOffice}
        disabled={busy}
        data-testid={`hall-seat-labels-print-${hall.id}`}
        title={`Sitzplan direkt an Bürodrucker senden (${seatCount} Etiketten · ${Math.ceil(seatCount / 24)} A4)`}
        className="text-xs px-2 py-1 text-[#D4AF37] border-l border-[#2A2A2A] hover:bg-[#D4AF37]/10 disabled:opacity-40 flex items-center gap-1">
        <Printer size={12} />
      </button>
    </div>
  );
}

function MiniPlan({ hall }) {
  const types = useMemo(() => Object.fromEntries((hall.seat_types || []).map((t) => [t.key, t])), [hall]);
  return (
    <div className="mt-4 p-3 bg-black/40 rounded border border-[#141414] relative">
      <DecorationLayer decorations={hall.decorations || []} />
      <div className="text-center text-[9px] uppercase tracking-[0.4em] text-[#575148] pb-1 border-b border-[#D4AF37]/30 mb-2">Leinwand</div>
      <div className="flex flex-col items-center gap-1">
        {(hall.rows || []).map((r) => (
          <div key={r.label} className="flex items-center gap-1">
            <span className="w-4 text-[9px] text-[#575148] font-mono">{r.label}</span>
            {r.seats.map((s) => {
              const t = types[s.kind || "regular"];
              const color = s.blocked ? "#3A3630" : (t?.color || "#F5F0E6");
              return <span key={s.n} className="w-3 h-3 rounded-sm" style={{ background: color, opacity: s.blocked ? 0.3 : (t?.sellable === false ? 0.35 : 0.9) }} />;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function HallEditor({ initial, onClose, onSaved }) {
  const containerRef = React.useRef(null);
  const [form, setForm] = useState({
    name: initial.name || "",
    hall: initial.hall || initial.name || "Saal 1",
    seat_types: (initial.seat_types && initial.seat_types.length > 0) ? initial.seat_types.map((t) => ({ ...t })) : DEFAULT_TYPES.map((t) => ({ ...t })),
    rows: (initial.rows || []).map((r) => ({ label: r.label, seats: r.seats.map((s) => ({ ...s })), angle: r.angle ?? 0, curve: (r.curve ?? null), layer_id: r.layer_id || null })),
    decorations: (initial.decorations || []).map((d) => ({ ...d, id: d.id || newDecoId() })),
    background_url: initial.background_url || "",
    background_opacity: initial.background_opacity ?? 0.35,
    layers: (initial.layers || []).map((l) => ({ ...l })),
    default_curve: initial.default_curve ?? 8,
    free_mode: initial.free_mode ?? false,
  });
  const [busy, setBusy] = useState(false);
  const [paintKind, setPaintKind] = useState("regular");
  const [paintMode, setPaintMode] = useState("kind"); // kind | block | delete
  const [view3d, setView3d] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [selectedDecoId, setSelectedDecoId] = useState(null);
  const [showDecoPanel, setShowDecoPanel] = useState(false);
  const [activeLayerId, setActiveLayerId] = useState(null); // null = alle Layer
  const [bgUploading, setBgUploading] = useState(false);
  const [imageDecoInputRef] = useState(() => React.createRef());
  const [pendingImageDecoId, setPendingImageDecoId] = useState(null);

  const newLayerId = () => `layer_${Math.random().toString(36).slice(2, 8)}`;
  const addLayer = () => setForm((f) => ({ ...f, layers: [...(f.layers || []), { id: newLayerId(), name: `Ebene ${(f.layers?.length || 0) + 1}`, color: "#4A9FE0" }] }));
  const removeLayer = (id) => setForm((f) => ({
    ...f,
    layers: (f.layers || []).filter((l) => l.id !== id),
    rows: (f.rows || []).map((r) => (r.layer_id === id ? { ...r, layer_id: null } : r)),
  }));
  const updateLayer = (id, patch) => setForm((f) => ({ ...f, layers: (f.layers || []).map((l) => l.id === id ? { ...l, ...patch } : l) }));

  const addDeco = (preset) => {
    if (preset.isImage) {
      // Zuerst File-Picker, dann Deko einfügen (via handleImageDecoPick)
      const deco = {
        id: newDecoId(),
        type: preset.type,
        x: preset.dw, y: preset.dh,
        scale: 1, rotation: 0,
        color: preset.defaultColor, opacity: 1,
        label: "", image_url: "",
      };
      setForm((f) => ({ ...f, decorations: [...(f.decorations || []), deco] }));
      setSelectedDecoId(deco.id);
      setShowDecoPanel(true);
      setPendingImageDecoId(deco.id);
      setTimeout(() => imageDecoInputRef.current?.click(), 30);
      return;
    }
    const deco = {
      id: newDecoId(),
      type: preset.type,
      x: preset.dw, y: preset.dh,
      scale: 1, rotation: 0,
      color: preset.defaultColor, opacity: 0.9,
      label: preset.hasText ? preset.label : "",
    };
    setForm((f) => ({ ...f, decorations: [...(f.decorations || []), deco] }));
    setSelectedDecoId(deco.id);
    setShowDecoPanel(true);
    toast.success(`+ ${preset.label}`);
  };
  const updateDeco = (id, patch) => setForm((f) => ({ ...f, decorations: (f.decorations || []).map((d) => d.id === id ? { ...d, ...patch } : d) }));
  const removeDeco = (id) => {
    setForm((f) => ({ ...f, decorations: (f.decorations || []).filter((d) => d.id !== id) }));
    if (selectedDecoId === id) setSelectedDecoId(null);
  };

  // ------- Upload-Helper (Hintergrundbild, Custom-Icons, Deko-Bilder) -----
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data?.url || data?.location || data?.path || "";
  };
  const onBackgroundPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max. 8 MB"); return; }
    setBgUploading(true);
    try {
      const url = await uploadFile(file);
      if (url) { upd({ background_url: url }); toast.success("Hintergrund gesetzt"); }
      else toast.error("Upload fehlgeschlagen");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"); }
    finally { setBgUploading(false); e.target.value = ""; }
  };
  const onCustomIconPick = (typeIdx) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Icon max. 2 MB"); return; }
    try {
      const url = await uploadFile(file);
      updType(typeIdx, { custom_icon_url: url });
      toast.success("Icon aktualisiert");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"); }
    finally { e.target.value = ""; }
  };
  const handleImageDecoPick = async (e) => {
    const file = e.target.files?.[0]; if (!file || !pendingImageDecoId) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Bild max. 5 MB"); return; }
    try {
      const url = await uploadFile(file);
      updateDeco(pendingImageDecoId, { image_url: url });
      toast.success("Bild gesetzt");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen"); }
    finally { setPendingImageDecoId(null); e.target.value = ""; }
  };

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addRow = () => upd({ rows: [...form.rows, { label: NEW_ROW_LABEL(form.rows), seats: [1,2,3,4].map((n) => ({ n, kind: paintKind, blocked: false })) }] });
  const removeRow = (idx) => upd({ rows: form.rows.filter((_, i) => i !== idx) });

  // Free-Mode: initialisiere x/y wenn noch nicht gesetzt, verteile Sitze auf einem Raster
  const seatDragRef = React.useRef(null);
  const initFreeCoords = React.useCallback(() => {
    setForm((f) => {
      if (!f.free_mode) return f;
      const rows = f.rows.length;
      const rowsNew = f.rows.map((r, ri) => {
        const cols = r.seats.length;
        const seats = r.seats.map((s, si) => {
          if (typeof s.x === "number" && typeof s.y === "number") return s;
          const x = cols > 1 ? 15 + (70 * si / (cols - 1)) : 50;
          const y = rows > 1 ? 25 + (55 * ri / (rows - 1)) : 50;
          return { ...s, x, y };
        });
        return { ...r, seats };
      });
      return { ...f, rows: rowsNew };
    });
  }, []);
  React.useEffect(() => { if (form.free_mode) initFreeCoords(); /* eslint-disable-next-line */ }, [form.free_mode]);

  const seatDragStart = (rIdx, sIdx) => (e) => {
    if (!form.free_mode) return;
    e.stopPropagation();
    const rect = seatDragRef.current?.getBoundingClientRect();
    if (!rect) return;
    const s = form.rows[rIdx].seats[sIdx];
    const dragData = { rIdx, sIdx, startX: e.clientX, startY: e.clientY, rect, sx: Number(s.x) || 50, sy: Number(s.y) || 50 };
    const move = (ev) => {
      const nx = Math.max(0, Math.min(100, dragData.sx + ((ev.clientX - dragData.startX) / dragData.rect.width) * 100));
      const ny = Math.max(0, Math.min(100, dragData.sy + ((ev.clientY - dragData.startY) / dragData.rect.height) * 100));
      setForm((f) => ({ ...f, rows: f.rows.map((rr, i) => i !== rIdx ? rr : { ...rr, seats: rr.seats.map((ss, j) => j !== sIdx ? ss : { ...ss, x: nx, y: ny }) }) }));
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const renameRow = (idx, label) => upd({ rows: form.rows.map((r, i) => i === idx ? { ...r, label: (label || "?").slice(0, 2).toUpperCase() } : r) });
  const addSeat = (idx) => upd({ rows: form.rows.map((r, i) => i === idx ? { ...r, seats: [...r.seats, { n: (r.seats.at(-1)?.n || 0) + 1, kind: paintKind, blocked: false }] } : r) });
  const removeSeat = (rIdx, sIdx) => upd({ rows: form.rows.map((r, i) => i === rIdx ? { ...r, seats: r.seats.filter((_, j) => j !== sIdx) } : r) });

  const paint = (rIdx, sIdx) => {
    const rows = form.rows.map((r, i) => {
      if (i !== rIdx) return r;
      const seats = r.seats.map((s, j) => {
        if (j !== sIdx) return s;
        if (paintMode === "block") return { ...s, blocked: !s.blocked };
        if (paintMode === "delete") return null;
        return { ...s, kind: paintKind };
      }).filter(Boolean);
      return { ...r, seats };
    });
    upd({ rows });
  };

  const addType = () => upd({ seat_types: [...form.seat_types, { key: `type${form.seat_types.length + 1}`, label: "Neu", color: "#D4AF37", surcharge: 0, override_price: null, sellable: true }] });

  // --- Ausrichtungs-Tools -------------------------------------------------
  // Alle Reihen auf max-Länge padden (fügt regulare Sitze hinten hinzu bis alle Reihen gleich lang sind)
  const alignRowsToMaxLength = () => {
    if (!form.rows.length) return;
    const max = Math.max(...form.rows.map((r) => r.seats.length));
    const rows = form.rows.map((r) => {
      if (r.seats.length >= max) return r;
      const nextN = (r.seats.at(-1)?.n || 0);
      const pad = Array.from({ length: max - r.seats.length }, (_, i) => ({
        n: nextN + i + 1, kind: paintKind, blocked: false,
      }));
      return { ...r, seats: [...r.seats, ...pad] };
    });
    upd({ rows });
    toast.success(`${form.rows.length} Reihen auf ${max} Sitze gepaddet`);
  };

  // Zentriert kürzere Reihen mit „gap"-Sitzen links/rechts (falls „gap" existiert, sonst blockiert)
  const centerRows = () => {
    if (!form.rows.length) return;
    const gapKind = form.seat_types.find((t) => t.key === "gap" || t.sellable === false)?.key || null;
    if (!gapKind) { toast.error("Erst einen 'Gang'/'Lücke' Typ hinzufügen"); return; }
    const max = Math.max(...form.rows.map((r) => r.seats.length));
    const rows = form.rows.map((r) => {
      const missing = max - r.seats.length;
      if (missing <= 0) return r;
      const left = Math.floor(missing / 2);
      const right = missing - left;
      const nextN = (r.seats.at(-1)?.n || 0);
      const leftPad = Array.from({ length: left }, (_, i) => ({ n: nextN + i + 1, kind: gapKind, blocked: false }));
      const rightPad = Array.from({ length: right }, (_, i) => ({ n: nextN + left + i + 1, kind: gapKind, blocked: false }));
      return { ...r, seats: [...leftPad, ...r.seats, ...rightPad] };
    });
    upd({ rows });
    toast.success("Reihen zentriert");
  };

  // Reihen A-Struktur übernehmen (Sitzanzahl + Typen)
  const applyFirstRowStructure = () => {
    if (form.rows.length < 2) return;
    const proto = form.rows[0];
    const rows = form.rows.map((r, i) => {
      if (i === 0) return r;
      const seats = proto.seats.map((s, j) => ({
        n: j + 1,
        kind: r.seats[j]?.kind || s.kind || "regular",
        blocked: r.seats[j]?.blocked || false,
      }));
      return { ...r, seats };
    });
    upd({ rows });
    toast.success(`Struktur von Reihe „${proto.label}" auf ${form.rows.length - 1} Reihen kopiert`);
  };

  // Reihe horizontal spiegeln
  const mirrorRow = (rIdx) => {
    const rows = form.rows.map((r, i) => i === rIdx ? {
      ...r,
      seats: r.seats.slice().reverse().map((s, j) => ({ ...s, n: j + 1 })),
    } : r);
    upd({ rows });
  };

  const removeType = (idx) => {
    const removed = form.seat_types[idx];
    // reassign any seat using this kind to "regular"
    const rows = form.rows.map((r) => ({ ...r, seats: r.seats.map((s) => s.kind === removed.key ? { ...s, kind: "regular" } : s) }));
    upd({ seat_types: form.seat_types.filter((_, i) => i !== idx), rows });
  };
  const updType = (idx, patch) => upd({ seat_types: form.seat_types.map((t, i) => i === idx ? { ...t, ...patch } : t) });

  const capacity = useMemo(() => {
    const typesMap = Object.fromEntries(form.seat_types.map((t) => [t.key, t]));
    let c = 0;
    form.rows.forEach((r) => r.seats.forEach((s) => {
      if (s.blocked) return;
      const t = typesMap[s.kind || "regular"];
      if (t && t.sellable === false) return;
      c++;
    }));
    return c;
  }, [form]);

  // Preis-Simulator: rechnet den Gesamt-Umsatz eines ausverkauften Saals
  // + Aufschlüsselung nach Sitzkategorie. Nutzt Standard-Ticketpreis aus /settings.
  const [basePrice, setBasePrice] = useState(8);
  useEffect(() => {
    api.get("/settings").then((r) => { if (r.data?.ticket_price) setBasePrice(Number(r.data.ticket_price)); }).catch(() => {});
  }, []);
  const priceSim = useMemo(() => {
    const typesMap = Object.fromEntries(form.seat_types.map((t) => [t.key, t]));
    const byKind = {};
    let total = 0, count = 0;
    form.rows.forEach((r) => r.seats.forEach((s) => {
      if (s.blocked) return;
      const kind = s.kind || "regular";
      const t = typesMap[kind];
      if (t && t.sellable === false) return;
      const price = (t?.override_price !== null && t?.override_price !== undefined && t?.override_price !== "")
        ? Number(t.override_price)
        : basePrice + Number(t?.surcharge || 0);
      byKind[kind] = byKind[kind] || { label: t?.label || kind, color: t?.color || "#F5F0E6", price, count: 0, sum: 0 };
      byKind[kind].count += 1;
      byKind[kind].sum += price;
      total += price;
      count += 1;
    }));
    return { total, count, byKind: Object.values(byKind).sort((a, b) => b.count - a.count) };
  }, [form, basePrice]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name des Saals fehlt");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        hall: form.hall.trim() || form.name.trim(),
        seat_types: form.seat_types.map((t) => ({
          key: t.key, label: t.label, color: t.color,
          surcharge: Number(t.surcharge) || 0,
          override_price: (t.override_price === "" || t.override_price === null || t.override_price === undefined) ? null : Number(t.override_price),
          sellable: !!t.sellable,
          icon: t.icon || "",
          custom_icon_url: t.custom_icon_url || null,
          snack_qr_enabled: t.snack_qr_enabled !== false,
        })),
        rows: form.rows.map((r) => ({
          label: r.label,
          seats: r.seats.map((s) => ({
            n: Number(s.n), kind: s.kind || "regular",
            blocked: !!s.blocked, label: s.label || "",
            ...(typeof s.x === "number" ? { x: Number(s.x) } : {}),
            ...(typeof s.y === "number" ? { y: Number(s.y) } : {}),
          })),
          angle: Number(r.angle) || 0,
          curve: (r.curve === "" || r.curve === null || r.curve === undefined) ? null : Number(r.curve),
          layer_id: r.layer_id || null,
        })),
        decorations: (form.decorations || []).map((d) => ({
          id: d.id, type: d.type,
          x: Number(d.x) || 50, y: Number(d.y) || 50,
          scale: Number(d.scale) || 1, rotation: Number(d.rotation) || 0,
          color: d.color || null, opacity: Number(d.opacity ?? 0.9),
          label: d.label || "",
          image_url: d.image_url || "",
        })),
        background_url: form.background_url || null,
        background_opacity: Number(form.background_opacity ?? 0.35),
        layers: (form.layers || []).map((l) => ({ id: l.id, name: l.name || "Ebene", color: l.color || "#4A9FE0" })),
        default_curve: Number(form.default_curve ?? 8),
        free_mode: !!form.free_mode,
      };
      if (initial.id) await api.patch(`/seat-plans/${initial.id}`, payload);
      else await api.post(`/seat-plans`, payload);
      toast.success("Saal gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm p-3 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-5xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-4" data-testid="hall-editor">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">{initial.id ? "Saal bearbeiten" : "Neuer Saal"}</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 grid lg:grid-cols-[minmax(0,300px)_1fr] gap-6">
          {/* Left: meta + seat types */}
          <div className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Name</label>
              <input data-testid="hall-name" value={form.name} onChange={(e) => upd({ name: e.target.value })} placeholder="z. B. Kellersaal"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Bezeichnung Vorstellung</label>
              <input value={form.hall} onChange={(e) => upd({ hall: e.target.value })} placeholder="z. B. Saal 1"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
              <div className="text-[10px] text-[#575148] mt-1">Wird auf Tickets/Displays angezeigt.</div>
            </div>

            {/* Hintergrund / Grundriss */}
            <div className="border-t border-[#1A1A1A] pt-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">Hintergrund / Grundriss</div>
              <label className="block cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={onBackgroundPick} data-testid="hall-bg-file" />
                <div className={`w-full aspect-[3/2] rounded border border-dashed ${form.background_url ? "border-[#D4AF37]/50" : "border-[#2A2A2A] hover:border-[#D4AF37]"} bg-[#050505] flex items-center justify-center overflow-hidden relative`}>
                  {form.background_url ? (
                    <>
                      <img src={form.background_url} alt="Hintergrund" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transitions-safe flex items-center justify-center text-[10px] uppercase tracking-widest text-[#EEDC82]">
                        {bgUploading ? "Lade …" : "Ändern"}
                      </div>
                    </>
                  ) : (
                    <div className="text-[10px] uppercase tracking-widest text-[#575148]">
                      {bgUploading ? "Lade …" : "+ Bild hochladen"}
                    </div>
                  )}
                </div>
              </label>
              {form.background_url && (
                <div className="grid grid-cols-[1fr_auto] gap-2 items-end mt-2">
                  <label className="text-[10px] text-[#A8A398]">
                    Transparenz · {(Number(form.background_opacity) * 100).toFixed(0)}%
                    <input type="range" min="0.05" max="1" step="0.05" value={form.background_opacity}
                      onChange={(e) => upd({ background_opacity: parseFloat(e.target.value) })} className="w-full" data-testid="hall-bg-opacity" />
                  </label>
                  <button onClick={() => upd({ background_url: "" })} className="text-[10px] px-2 py-1 border border-[#7A1921]/60 text-[#EE9CA1] rounded hover:bg-[#7A1921]/10" data-testid="hall-bg-remove">
                    <Trash2 size={11} className="inline" />
                  </button>
                </div>
              )}
            </div>

            {/* Ebenen (Multi-Layer: EG / Balkon / VIP-Etage …) */}
            <div className="border-t border-[#1A1A1A] pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Ebenen · {(form.layers || []).length || 1}</div>
                <button onClick={addLayer} className="text-xs text-[#D4AF37] hover:text-[#EEDC82]" data-testid="hall-layer-add"><Plus size={12} className="inline" /> Ebene</button>
              </div>
              <div className="space-y-1.5">
                {(form.layers || []).length === 0 && <div className="text-[10px] text-[#575148]">Keine Ebenen — alle Reihen liegen auf einer Fläche.</div>}
                {(form.layers || []).map((l) => {
                  const isActive = activeLayerId === l.id;
                  return (
                    <div key={l.id} className="flex items-center gap-2 bg-[#050505] border border-[#1A1A1A] rounded p-1.5">
                      <input type="color" value={l.color} onChange={(e) => updateLayer(l.id, { color: e.target.value })}
                        className="w-6 h-6 rounded cursor-pointer bg-transparent border border-[#2A2A2A]" />
                      <input value={l.name} onChange={(e) => updateLayer(l.id, { name: e.target.value })}
                        className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs outline-none focus:border-[#D4AF37]" />
                      <button onClick={() => setActiveLayerId(isActive ? null : l.id)}
                        title={isActive ? "Alle anzeigen" : "Nur diese Ebene"}
                        data-testid={`hall-layer-focus-${l.id}`}
                        className={`text-[10px] px-2 py-1 rounded border ${isActive ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-[#A8A398] border-[#2A2A2A] hover:border-[#D4AF37]"}`}>
                        {isActive ? "aktiv" : "fokus"}
                      </button>
                      <button onClick={() => removeLayer(l.id)} className="text-[#EE9CA1] px-1"><Trash2 size={11} /></button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2">
                <label className="text-[10px] text-[#A8A398]">
                  Standard-Bogen aller Reihen · {Number(form.default_curve).toFixed(0)}°
                  <input type="range" min="0" max="30" step="1" value={form.default_curve}
                    onChange={(e) => upd({ default_curve: parseFloat(e.target.value) })} className="w-full" data-testid="hall-default-curve" />
                </label>
              </div>
              <label className="mt-3 flex items-center gap-2 text-[10px] text-[#A8A398] cursor-pointer" data-testid="hall-free-mode">
                <input type="checkbox" checked={!!form.free_mode} onChange={(e) => upd({ free_mode: e.target.checked })} />
                <span>Freie Sitz-Platzierung (Lounge-Cluster, Sofa-Inseln)</span>
              </label>
              {form.free_mode && (
                <div className="mt-1 text-[10px] text-[#EEDC82] bg-[#8B7328]/15 border border-[#D4AF37]/30 rounded p-2 leading-relaxed">
                  ⚡ Freier Modus aktiv — Sitze werden mit <b>x/y-Koordinaten</b> gespeichert. Ziehe im Sitzplan die Sitze frei umher (Deko-Handles zeigen dir wie). Reihen bleiben als „Gruppe" bestehen für Reihen-Beschriftung.
                </div>
              )}
            </div>

            <div className="border-t border-[#1A1A1A] pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Sitztypen · {form.seat_types.length}</div>
                <button onClick={addType} className="text-xs text-[#D4AF37] hover:text-[#EEDC82]"><Plus size={12} className="inline" /> Typ</button>
              </div>

              {/* Preset-Buttons für gängige Sitztypen */}
              <div className="mb-3 flex flex-wrap gap-1.5">
                {SEAT_PRESETS.map((preset) => {
                  const exists = form.seat_types.some((t) => t.key === preset.key);
                  return (
                    <button key={preset.key}
                      onClick={() => {
                        if (exists) { toast.info("Typ existiert bereits"); return; }
                        upd({ seat_types: [...form.seat_types, { ...preset }] });
                        toast.success(`+ ${preset.label}`);
                      }}
                      disabled={exists}
                      data-testid={`seat-preset-${preset.key}`}
                      className={`text-[10px] px-2 py-1 rounded border transitions-safe ${exists ? "opacity-30 cursor-not-allowed" : "hover:bg-[#D4AF37]/10"}`}
                      style={{ borderColor: preset.color, color: preset.color }}
                      title={preset.override_price === 0 ? "Kostenlos" : preset.surcharge ? `+${preset.surcharge}€ Aufpreis` : ""}>
                      + {preset.label}
                    </button>
                  );
                })}
              </div>
              <div className="space-y-2">
                {form.seat_types.map((t, i) => (
                  <div key={i} className="bg-[#050505] border border-[#1A1A1A] rounded p-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <input type="color" value={t.color} onChange={(e) => updType(i, { color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer bg-transparent border border-[#2A2A2A]" />
                      <input value={t.label} onChange={(e) => updType(i, { label: e.target.value })} placeholder="Label"
                        className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-sm outline-none focus:border-[#D4AF37]" />
                      {t.key !== "regular" && (
                        <button onClick={() => removeType(i)} className="text-[#EE9CA1] px-1"><Trash2 size={12} /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[10px]">
                      <label className="flex items-center gap-1 text-[#A8A398]">
                        Aufpreis (€)
                        <input type="number" step="0.5" value={t.surcharge ?? 0} onChange={(e) => updType(i, { surcharge: parseFloat(e.target.value || 0) })}
                          className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
                      </label>
                      <label className="flex items-center gap-1 text-[#A8A398]">
                        Fixpreis
                        <input type="number" step="0.5" value={t.override_price ?? ""} placeholder="—" onChange={(e) => updType(i, { override_price: e.target.value === "" ? null : parseFloat(e.target.value) })}
                          className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
                      </label>
                    </div>
                    <label className="text-[10px] flex items-center gap-1 text-[#A8A398]">
                      <input type="checkbox" checked={t.sellable !== false} onChange={(e) => updType(i, { sellable: e.target.checked })} />
                      Buchbar (Gang/Lücke → aus)
                    </label>
                    <label className="text-[10px] flex items-center gap-1 text-[#A8A398]" title="Erzeugt für Sitze dieser Art einen Snack-am-Platz QR-Code">
                      <input type="checkbox" checked={t.snack_qr_enabled !== false} onChange={(e) => updType(i, { snack_qr_enabled: e.target.checked })}
                        data-testid={`seat-type-snack-qr-${t.key}`} />
                      Snack-am-Platz QR erlauben
                    </label>
                    {/* Custom-Icon Upload */}
                    <div className="mt-1 flex items-center gap-2">
                      <label className="text-[10px] text-[#A8A398] flex items-center gap-1 cursor-pointer">
                        <input type="file" accept="image/svg+xml,image/png,image/webp" className="hidden" onChange={onCustomIconPick(i)} data-testid={`seat-type-icon-file-${t.key}`} />
                        <span className="px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] hover:text-[#D4AF37]">
                          {t.custom_icon_url ? "Icon ändern" : "Eigenes Icon"}
                        </span>
                      </label>
                      {t.custom_icon_url && (
                        <>
                          <img src={t.custom_icon_url} alt="" className="w-5 h-5 object-contain rounded border border-[#2A2A2A] bg-[#0A0A0A]" />
                          <button onClick={() => updType(i, { custom_icon_url: null })} className="text-[#EE9CA1] text-[10px]"><Trash2 size={10} className="inline" /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: seat plan editor */}
          <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A8A398]">Sitzplan · {capacity} buchbare Sitze · Basispreis <span className="text-[#D4AF37] font-mono">{basePrice.toFixed(2)} €</span></div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setView3d(!view3d)} data-testid="hall-3d-toggle"
                  className={`text-xs border rounded px-2 py-1 inline-flex items-center gap-1 transitions-safe ${view3d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-[#D4AF37] border-[#D4AF37]/40 hover:bg-[#D4AF37]/10"}`}
                  title="Isometrische 3D-Vorschau">
                  <Cuboid size={12} /> 3D
                </button>
                <button onClick={() => setShowGrid(!showGrid)}
                  className={`text-xs border rounded px-2 py-1 inline-flex items-center gap-1 transitions-safe ${showGrid ? "border-[#EEDC82] text-[#EEDC82] bg-[#8B7328]/10" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]"}`}
                  title="Grid-Markierungen für Alignment">
                  Grid
                </button>
                <select value={paintMode} onChange={(e) => setPaintMode(e.target.value)}
                  className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs">
                  <option value="kind">Sitztyp setzen</option>
                  <option value="block">Blockieren umschalten</option>
                  <option value="delete">Sitz löschen</option>
                </select>
                {paintMode === "kind" && (
                  <select value={paintKind} onChange={(e) => setPaintKind(e.target.value)}
                    className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs">
                    {form.seat_types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                )}
                <button onClick={addRow} data-testid="hall-add-row" className="text-xs text-[#D4AF37] border border-[#D4AF37]/40 rounded px-2 py-1 hover:bg-[#D4AF37]/10"><Plus size={12} className="inline" /> Reihe</button>
              </div>
            </div>

            {/* Preis-Simulator */}
            <div className="mb-3 grid sm:grid-cols-[1fr_auto] gap-3 items-stretch" data-testid="hall-price-sim">
              <div className="rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-3">
                <div className="text-[9px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2">Preis-Simulator (ausverkaufter Saal)</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {priceSim.byKind.map((k) => (
                    <div key={k.label} className="text-[10px]">
                      <span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: k.color }} />
                      <span className="text-[#A8A398]">{k.label} × {k.count}</span>
                      <span className="text-[#EEDC82] font-mono ml-1">{k.price.toFixed(2)} €</span>
                      <span className="text-[#575148] ml-1">→ {k.sum.toFixed(2)} €</span>
                    </div>
                  ))}
                  {!priceSim.byKind.length && <div className="text-[10px] text-[#575148]">Sitze hinzufügen für Simulation …</div>}
                </div>
              </div>
              <div className="rounded-lg border border-[#D4AF37] bg-gradient-to-br from-[#8B7328]/20 to-[#D4AF37]/10 p-3 flex flex-col items-center justify-center min-w-[160px]">
                <div className="text-[9px] uppercase tracking-[0.32em] text-[#D4AF37]">Summe</div>
                <div className="font-heading text-3xl text-[#EEDC82]" data-testid="hall-price-total">{priceSim.total.toFixed(2)} €</div>
                <div className="text-[9px] text-[#A8A398]">{priceSim.count} Tickets</div>
              </div>
            </div>

            {/* Ausrichtungs-Toolbar */}
            <div className="mb-3 flex flex-wrap gap-1.5 items-center text-[10px]">
              <span className="text-[#575148] uppercase tracking-widest mr-1">Ausrichten:</span>
              <button onClick={alignRowsToMaxLength} data-testid="hall-align-max"
                className="px-2 py-1 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37] inline-flex items-center gap-1">
                <ArrowRightLeft size={11} /> Alle gleich lang
              </button>
              <button onClick={centerRows} data-testid="hall-align-center"
                className="px-2 py-1 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37] inline-flex items-center gap-1">
                <AlignCenter size={11} /> Zentrieren
              </button>
              <button onClick={applyFirstRowStructure} data-testid="hall-align-first"
                className="px-2 py-1 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                title="Sitzanzahl+Typen aus Reihe A übernehmen">
                Struktur von A übernehmen
              </button>
            </div>

            {/* Deko-Bibliothek Toolbar */}
            <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[10px]">
              <button onClick={() => setShowDecoPanel(!showDecoPanel)} data-testid="hall-deco-toggle"
                className={`px-2 py-1 border rounded inline-flex items-center gap-1 transitions-safe ${showDecoPanel ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                <Sparkles size={11} /> Deko-Bibliothek {form.decorations?.length ? `(${form.decorations.length})` : ""}
              </button>
              {showDecoPanel && DECO_PRESETS.map((p) => (
                <button key={p.type} onClick={() => addDeco(p)} data-testid={`hall-deco-add-${p.type}`}
                  className="px-2 py-1 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  style={{ borderColor: p.defaultColor, color: p.defaultColor }}>
                  + {p.label}
                </button>
              ))}
            </div>

            {/* Deko-Details für die ausgewählte Deko */}
            {showDecoPanel && form.decorations?.length > 0 && (
              <div className="mb-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-1">Platzierte Deko · {form.decorations.length}</div>
                <div className="flex flex-wrap gap-1.5">
                  {form.decorations.map((d) => {
                    const p = DECO_PRESETS.find((x) => x.type === d.type);
                    const isSel = selectedDecoId === d.id;
                    return (
                      <button key={d.id} onClick={() => setSelectedDecoId(isSel ? null : d.id)}
                        data-testid={`hall-deco-select-${d.id}`}
                        className={`text-[10px] px-2 py-1 rounded border transitions-safe ${isSel ? "border-[#D4AF37] bg-[#D4AF37]/20 text-[#EEDC82]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60"}`}>
                        {p?.label || d.type}
                      </button>
                    );
                  })}
                </div>
                {selectedDecoId && (() => {
                  const d = form.decorations.find((x) => x.id === selectedDecoId);
                  if (!d) return null;
                  const p = DECO_PRESETS.find((x) => x.type === d.type);
                  return (
                    <div className="mt-2 pt-2 border-t border-[#D4AF37]/20 grid grid-cols-2 md:grid-cols-6 gap-2 items-end text-[10px]">
                      <label className="col-span-1">
                        <div className="text-[#A8A398] mb-0.5">X · {Number(d.x).toFixed(0)}%</div>
                        <input type="range" min="0" max="100" step="0.5" value={d.x}
                          onChange={(e) => updateDeco(d.id, { x: parseFloat(e.target.value) })}
                          className="w-full" data-testid="hall-deco-x" />
                      </label>
                      <label className="col-span-1">
                        <div className="text-[#A8A398] mb-0.5">Y · {Number(d.y).toFixed(0)}%</div>
                        <input type="range" min="0" max="100" step="0.5" value={d.y}
                          onChange={(e) => updateDeco(d.id, { y: parseFloat(e.target.value) })}
                          className="w-full" data-testid="hall-deco-y" />
                      </label>
                      <label className="col-span-1">
                        <div className="text-[#A8A398] mb-0.5">Größe · {Number(d.scale).toFixed(2)}×</div>
                        <input type="range" min="0.2" max="4" step="0.05" value={d.scale}
                          onChange={(e) => updateDeco(d.id, { scale: parseFloat(e.target.value) })}
                          className="w-full" data-testid="hall-deco-scale" />
                      </label>
                      <label className="col-span-1">
                        <div className="text-[#A8A398] mb-0.5">Rotation · {Number(d.rotation).toFixed(0)}°</div>
                        <input type="range" min="-180" max="180" step="5" value={d.rotation}
                          onChange={(e) => updateDeco(d.id, { rotation: parseFloat(e.target.value) })}
                          className="w-full" data-testid="hall-deco-rot" />
                      </label>
                      <label className="col-span-1 flex flex-col">
                        <div className="text-[#A8A398] mb-0.5">Farbe</div>
                        <input type="color" value={d.color || p?.defaultColor || "#D4AF37"}
                          onChange={(e) => updateDeco(d.id, { color: e.target.value })}
                          className="w-full h-6 bg-transparent border border-[#2A2A2A] rounded cursor-pointer" />
                      </label>
                      <div className="col-span-1 flex gap-1">
                        <button onClick={() => updateDeco(d.id, { rotation: (Number(d.rotation) + 90) % 360 })}
                          className="px-1.5 py-1 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" title="90° drehen">
                          <RotateCw size={11} />
                        </button>
                        <button onClick={() => removeDeco(d.id)} data-testid="hall-deco-delete"
                          className="px-1.5 py-1 border border-[#7A1921]/60 text-[#EE9CA1] rounded hover:bg-[#7A1921]/20">
                          <Trash2 size={11} />
                        </button>
                      </div>
                      {p?.hasText && (
                        <label className="col-span-full">
                          <div className="text-[#A8A398] mb-0.5">Text-Inhalt</div>
                          <input type="text" value={d.label || ""} maxLength={40}
                            onChange={(e) => updateDeco(d.id, { label: e.target.value })}
                            placeholder="z. B. Eingang · Loge · VIP"
                            className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs outline-none focus:border-[#D4AF37]" />
                        </label>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            <div ref={containerRef} className={`rounded-lg border border-[#1A1A1A] bg-black/60 p-4 relative ${view3d ? "seatmap-3d" : ""}`}>
              {/* Hintergrundbild */}
              {form.background_url && !view3d && (
                <div aria-hidden style={{
                  position: "absolute", inset: 0, borderRadius: "0.5rem",
                  backgroundImage: `url(${form.background_url})`,
                  backgroundSize: "cover", backgroundPosition: "center",
                  opacity: Number(form.background_opacity ?? 0.35), pointerEvents: "none",
                }} />
              )}
              {/* Deko-Overlay mit Drag & Drop */}
              {form.decorations?.length > 0 && !view3d && (
                <DecorationLayer decorations={form.decorations} selectedId={selectedDecoId} onSelect={setSelectedDecoId}
                  onUpdate={updateDeco} draggable containerRef={containerRef} />
              )}
              {/* Grid Guides */}
              {showGrid && !view3d && (
                <div className="absolute inset-4 pointer-events-none opacity-20"
                  style={{ backgroundImage: "repeating-linear-gradient(90deg, #D4AF37 0, #D4AF37 1px, transparent 1px, transparent 34px)" }} />
              )}
              {/* Kinepolis-Style Screen */}
              <div className="mx-auto mb-4 max-w-md">
                <div className="mx-auto h-1.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent rounded-full" />
                <div className="text-center text-[10px] uppercase tracking-[0.4em] text-[#575148] mt-1">LEINWAND</div>
              </div>
              {view3d && <div className="text-center text-[9px] normal-case text-[#D4AF37] mb-2">👁 Isometrische Vorschau · Klick funktioniert wie normal</div>}
              {/* Isometric-Container / Free-Mode-Canvas */}
              <div ref={seatDragRef} className={form.free_mode ? "relative mx-auto" : (view3d ? "seatmap-3d__stage" : "")}
                style={form.free_mode ? { minHeight: 480, border: "1px dashed #2A2A2A", borderRadius: 8 } : undefined}>
                {form.free_mode && form.rows.flatMap((r) => r.seats.map((s) => ({ r, s }))).map(({ r, s }) => {
                  const rIdx = form.rows.indexOf(r);
                  const sIdx = r.seats.indexOf(s);
                  const kind = s.kind || "regular";
                  const t = form.seat_types.find((x) => x.key === kind);
                  const isWheelchair = t?.icon === "wheelchair" || kind === "wheelchair";
                  const isSofa = t?.icon === "sofa" || kind === "sofa";
                  const isLoveseat = t?.icon === "heart" || kind === "loveseat";
                  const isGap = t?.sellable === false;
                  const kioskColor = isWheelchair ? "#1F71C6" : (kind === "regular") ? "#4A9FE0" : (t?.color || "#4A9FE0");
                  const w = isSofa ? 64 : isLoveseat ? 68 : 32;
                  const h = 32;
                  return (
                    <button key={`${r.label}-${s.n}-free`}
                      onPointerDown={seatDragStart(rIdx, sIdx)}
                      onClick={(e) => { if (!e.shiftKey) return; paint(rIdx, sIdx); }}
                      data-testid={`hall-freeseat-${r.label}${s.n}`}
                      title={`${r.label}${s.n} · ${t?.label || kind} · Drag zum Verschieben · Shift+Klick zum Bemalen`}
                      className="rounded-md text-[10px] font-mono transitions-safe flex items-center justify-center absolute"
                      style={{
                        left: `${s.x ?? 50}%`, top: `${s.y ?? 50}%`,
                        transform: "translate(-50%, -50%)",
                        width: w, height: h,
                        background: s.blocked ? "#26221C" : isGap ? "transparent" : kioskColor,
                        color: s.blocked || isGap ? "#575148" : "#FFFFFF",
                        border: isGap ? "1px dashed #2A2A2A" : `1px solid ${s.blocked ? "#3A3630" : "rgba(255,255,255,0.35)"}`,
                        borderRadius: isSofa || isLoveseat ? "14px 14px 4px 4px" : "6px 6px 4px 4px",
                        cursor: "grab", touchAction: "none", userSelect: "none",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                      }}>
                      {t?.custom_icon_url ? <img src={t.custom_icon_url} alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
                        : <span className="opacity-90">{r.label}{s.n}</span>}
                    </button>
                  );
                })}
                {form.free_mode && !form.rows.some((r) => r.seats.length) && (
                  <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#575148]">Füge oben Reihen mit Sitzen hinzu — sie erscheinen hier als frei verschiebbare Kacheln.</div>
                )}
                {!form.free_mode && (
                <div className={`space-y-1.5 ${view3d ? "seatmap-3d__plane" : ""}`}>
                  {form.rows.filter((r) => !activeLayerId || (r.layer_id || (form.layers?.[0]?.id ?? null)) === activeLayerId).map((r) => {
                    const rIdx = form.rows.indexOf(r);
                    const isMaxLen = form.rows.length > 1 && r.seats.length === Math.max(...form.rows.map((x) => x.seats.length));
                    const rowLayer = (form.layers || []).find((l) => l.id === r.layer_id);
                    return (
                      <div key={rIdx} className="flex items-center gap-2 relative"
                        style={r.angle ? { transform: `rotate(${Number(r.angle) || 0}deg)`, transformOrigin: "left center" } : undefined}>
                        <input value={r.label} onChange={(e) => renameRow(rIdx, e.target.value)}
                          className="w-8 text-center bg-[#050505] border border-[#2A2A2A] rounded font-mono text-xs py-1" />
                        <div className={`flex-1 flex gap-1 flex-wrap ${showGrid && !isMaxLen ? "ring-1 ring-[#EE9CA1]/30 rounded p-0.5" : ""}`}>
                          {r.seats.map((s, sIdx) => {
                            const kind = s.kind || "regular";
                            const t = form.seat_types.find((x) => x.key === kind);
                            const isWheelchair = t?.icon === "wheelchair" || kind === "wheelchair";
                            const isSofa = t?.icon === "sofa" || kind === "sofa";
                            const isLoveseat = t?.icon === "heart" || kind === "loveseat";
                            const isGap = t?.sellable === false;
                            const kioskColor = isWheelchair ? "#1F71C6"
                              : (kind === "regular") ? "#4A9FE0"
                              : (t?.color || "#4A9FE0");
                            const w = isSofa ? "w-16" : isLoveseat ? "w-[68px]" : "w-8";
                            const extraSeatCls = [
                              isSofa ? "kiosk-editor-seat--sofa" : "",
                              isLoveseat ? "kiosk-editor-seat--loveseat" : "",
                            ].filter(Boolean).join(" ");
                            const dataCls = `h-8 ${w} kiosk-editor-seat ${extraSeatCls}`.trim();
                            return (
                              <button
                                key={sIdx}
                                onClick={() => paint(rIdx, sIdx)}
                                data-testid={`hall-seat-${r.label}${s.n}`}
                                title={`${t?.label || kind}${t?.surcharge ? ` +${t.surcharge}€` : ""}${t?.override_price === 0 ? " · kostenlos" : ""}`}
                                className={`${dataCls} rounded-md text-[10px] font-mono transitions-safe relative flex items-center justify-center ${s.blocked ? "kiosk-editor-seat--blocked" : isGap ? "kiosk-editor-seat--gap" : ""}`}
                                style={{
                                  background: s.blocked ? "#26221C" : isGap ? "transparent" : kioskColor,
                                  color: s.blocked || isGap ? "#575148" : "#FFFFFF",
                                  border: isGap ? "1px dashed #2A2A2A" : `1px solid ${s.blocked ? "#3A3630" : "rgba(255,255,255,0.25)"}`,
                                  borderRadius: isSofa || isLoveseat ? "14px 14px 4px 4px" : "6px 6px 4px 4px",
                                }}
                              >
                                {t?.custom_icon_url ? (
                                  <img src={t.custom_icon_url} alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
                                ) : isWheelchair && !s.blocked ? (
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="white" aria-hidden>
                                    <path d="M12 4a2 2 0 100 4 2 2 0 000-4zm-2.4 4.2c-.8 0-1.5.5-1.7 1.3l-.7 3.4c-.1.6.4 1.1 1 1.1h2.4l-.7 3.4h-2.4c-1.7 0-3.1 1.4-3.1 3.1S5.5 23.6 7.2 23.6c1.4 0 2.6-.9 3-2.2h6l1.2-3H12l.4-2H15c.5 0 1-.4 1-1s-.5-1-1-1h-2.2l.5-2.4c.2-1.1-.6-2.2-1.7-2.2z"/>
                                  </svg>
                                ) : (
                                  <span className="opacity-90">{s.n}</span>
                                )}
                              </button>
                            );
                          })}
                          <button onClick={() => addSeat(rIdx)} className="w-8 h-8 border border-dashed border-[#2A2A2A] rounded text-[#575148] hover:border-[#D4AF37] hover:text-[#D4AF37]"><Plus size={12} className="mx-auto" /></button>
                        </div>
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="flex gap-1">
                            <input type="number" step="5" min="-180" max="180" value={r.angle ?? 0}
                              onChange={(e) => upd({ rows: form.rows.map((x, ii) => ii === rIdx ? { ...x, angle: parseFloat(e.target.value || 0) } : x) })}
                              title="Reihen-Rotation in Grad"
                              className="w-12 bg-[#050505] border border-[#2A2A2A] rounded px-1 py-0.5 text-[10px] font-mono outline-none focus:border-[#D4AF37]"
                              data-testid={`hall-row-angle-${rIdx}`} />
                            <input type="number" step="1" min="0" max="30" value={r.curve ?? ""}
                              placeholder="⤴"
                              onChange={(e) => upd({ rows: form.rows.map((x, ii) => ii === rIdx ? { ...x, curve: e.target.value === "" ? null : parseFloat(e.target.value) } : x) })}
                              title="Bogen (überschreibt Standard) – leer = Standard"
                              className="w-10 bg-[#050505] border border-[#2A2A2A] rounded px-1 py-0.5 text-[10px] font-mono outline-none focus:border-[#D4AF37]"
                              data-testid={`hall-row-curve-${rIdx}`} />
                            {(form.layers || []).length > 0 && (
                              <select value={r.layer_id || ""} onChange={(e) => upd({ rows: form.rows.map((x, ii) => ii === rIdx ? { ...x, layer_id: e.target.value || null } : x) })}
                                title="Ebene"
                                className="bg-[#050505] border border-[#2A2A2A] rounded px-1 py-0.5 text-[10px] outline-none focus:border-[#D4AF37]"
                                data-testid={`hall-row-layer-${rIdx}`}>
                                <option value="">— Ebene —</option>
                                {form.layers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                              </select>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => mirrorRow(rIdx)} className="text-[#A8A398] text-xs border border-[#2A2A2A] rounded px-1.5 py-0.5 hover:border-[#D4AF37] hover:text-[#D4AF37]" title="Reihe spiegeln"><FlipHorizontal size={11} /></button>
                            <button onClick={() => removeRow(rIdx)} className="text-[#EE9CA1] text-xs border border-[#2A2A2A] rounded px-1.5 py-0.5 hover:border-[#7A1921]"><Trash2 size={11} /></button>
                          </div>
                        </div>
                        {rowLayer && (
                          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-6 rounded" style={{ background: rowLayer.color }} title={rowLayer.name} />
                        )}
                        {showGrid && !isMaxLen && (
                          <div className="absolute -right-2 top-1/2 -translate-y-1/2 text-[9px] text-[#EE9CA1] font-mono bg-[#5B1319]/40 px-1 py-0.5 rounded" title="Diese Reihe ist kürzer als die längste">
                            {r.seats.length}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
              {!form.rows.length && <div className="text-center text-xs text-[#A8A398] py-6">Noch keine Reihen. Klick auf &bdquo;+ Reihe&ldquo;.</div>}
            </div>
            {/* Verstecktes File-Input für Bild-Deko */}
            <input ref={imageDecoInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageDecoPick} />
            <div className="text-[10px] text-[#575148] mt-3">Ansicht wie im Ticket-Kiosk: blaue Sitze = frei, weißer Rollstuhl = Rollstuhl-Platz, dunkel = blockiert, gestrichelt = Gang/Lücke. <span className="text-[#D4AF37]">3D</span> zeigt Perspektive · <span className="text-[#EEDC82]">Grid</span> markiert kürzere Reihen rot · <span className="text-[#EEDC82]">Free-Mode</span> → Sitze frei per Drag verschieben, Shift+Klick zum Bemalen.</div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button data-testid="hall-save-btn" onClick={save} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2">
            <Save size={14} /> {busy ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
